// src/component/hooks/useFeedbackTicketDetail.js
import { useCallback, useEffect, useRef, useState } from 'react';
import useApiService from './useApiService';
import {
  fetchFeedbackThread,
  fetchFeedbackThreadMessages,
  mapMessage,
  mapThread,
  postFeedbackThreadMessage,
  updateFeedbackThreadStatus,
} from '../../services/feedbackSupport';

const POLL_INTERVAL = 15_000; // 15 seconds
const MESSAGE_PAGE_SIZE = 50;

const extractError = (error, fallback) =>
  error?.response?.data?.error?.message ||
  error?.message ||
  fallback;

export const mergeMessages = (existing = [], incoming = []) => {
  if (!existing.length && !incoming.length) return [];
  const registry = new Map();
  existing.forEach((msg) => {
    if (msg?.id) registry.set(msg.id, msg);
  });
  incoming.forEach((msg) => {
    if (msg?.id) registry.set(msg.id, msg);
  });
  return Array.from(registry.values()).sort((a, b) => {
    const aTime = new Date(a?.createdAt || '').getTime();
    const bTime = new Date(b?.createdAt || '').getTime();
    return aTime - bTime;
  });
};

const mergeThreadAndMessages = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return { thread: null, messages: [] };
  }

  const threadCandidate = payload.thread || payload;
  const messagesCandidate = Array.isArray(payload.messages)
    ? payload.messages
    : Array.isArray(threadCandidate?.messages)
      ? threadCandidate.messages
      : [];

  return {
    thread: mapThread(threadCandidate),
    messages: messagesCandidate.map(mapMessage).filter(Boolean),
  };
};

export default function useFeedbackTicketDetail(threadId) {
  const {
    executeGet,
    executePost,
  } = useApiService();

  const getRef = useRef(executeGet);
  const postRef = useRef(executePost);

  useEffect(() => { getRef.current = executeGet; }, [executeGet]);
  useEffect(() => { postRef.current = executePost; }, [executePost]);

  const [detail, setDetail] = useState({ thread: null, messages: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchFeedbackThread(getRef.current, threadId);
      const payload = res?.data ?? res;
      const merged = mergeThreadAndMessages(payload);
      setDetail({
        thread: merged.thread,
        messages: merged.messages,
        total: payload?.total ?? merged.messages.length,
      });
    } catch (err) {
      setError(extractError(err, 'Feedback ticket bilgisi alınamadı.'));
      setDetail({ thread: null, messages: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    let cancelled = false;
    if (!threadId) return () => { cancelled = true; };

    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchFeedbackThread(getRef.current, threadId);
        if (cancelled) return;

        const payload = res?.data ?? res;
        const merged = mergeThreadAndMessages(payload);
        setDetail({
          thread: merged.thread,
          messages: merged.messages,
          total: payload?.total ?? merged.messages.length,
        });
      } catch (err) {
        if (!cancelled) setError(extractError(err, 'Feedback ticket bilgisi alınamadı.'));
        if (!cancelled) setDetail({ thread: null, messages: [], total: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [threadId]);

  const sendMessage = useCallback(async (message) => {
    if (!threadId) return { ok: false, message: 'Geçersiz ticket.' };
    const trimmed = (message ?? '').trim();
    if (!trimmed) return { ok: false, message: 'Lütfen mesajınızı yazın.' };

    setSending(true);
    try {
      const res = await postFeedbackThreadMessage(postRef.current, threadId, {
        message: trimmed,
      });

      const payload = res?.data ?? res;
      const mapped = mapMessage(payload?.message ?? payload);
      if (!mapped) {
        return { ok: true };
      }

      setDetail((prev) => {
        if (!prev.thread) return prev;
        const nextMessages = mergeMessages(prev.messages, [mapped]);
        const mappedThread = payload?.thread ? mapThread(payload.thread) : null;
        return {
          thread: mappedThread ? { ...prev.thread, ...mappedThread } : {
            ...prev.thread,
            updatedAt: mapped.createdAt ?? prev.thread.updatedAt,
          },
          messages: nextMessages,
          total: Math.max(nextMessages.length, prev.total ?? nextMessages.length),
        };
      });

      return { ok: true };
    } catch (err) {
      const msg = extractError(err, 'Mesaj gönderilemedi.');
      return { ok: false, message: msg };
    } finally {
      setSending(false);
    }
  }, [threadId]);

  const loadMore = useCallback(async () => {
    if (!threadId) return { ok: false };
    if (loadingMore) return { ok: false };
    setLoadingMore(true);
    try {
      const offset = detail.messages.length;
      const res = await fetchFeedbackThreadMessages(getRef.current, threadId, {
        limit: MESSAGE_PAGE_SIZE,
        offset,
      });
      const payload = res?.data ?? res;
      const items = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.messages)
          ? payload.messages
          : Array.isArray(payload)
            ? payload
            : [];
      const mapped = items.map(mapMessage).filter(Boolean);
      if (!mapped.length) {
        setLoadingMore(false);
        return { ok: true };
      }
      setDetail((prev) => {
        if (!prev.thread) return prev;
        const nextMessages = mergeMessages(prev.messages, mapped);
        return {
          thread: prev.thread,
          messages: nextMessages,
          total: payload?.total ?? prev.total ?? nextMessages.length,
        };
      });
      setLoadingMore(false);
      return { ok: true };
    } catch (err) {
      setLoadingMore(false);
      const msg = extractError(err, 'Mesajlar yüklenemedi.');
      setError(msg);
      return { ok: false, message: msg };
    }
  }, [threadId, loadingMore, detail.messages.length]);

  const changeStatus = useCallback(async (status) => {
    if (!threadId) return { ok: false, message: 'Geçersiz ticket.' };
    const nextStatus = String(status ?? '').trim();
    if (!nextStatus) return { ok: false, message: 'Durum seçiniz.' };

    setUpdatingStatus(true);
    try {
      const res = await updateFeedbackThreadStatus(postRef.current, threadId, nextStatus);
      const payload = res?.data ?? res;
      const mapped = payload?.thread ? mapThread(payload.thread) : null;
      setDetail((prev) => {
        if (!prev.thread) return prev;
        return {
          ...prev,
          thread: mapped ? { ...prev.thread, ...mapped } : { ...prev.thread, status: nextStatus },
        };
      });
      return { ok: true };
    } catch (err) {
      const msg = extractError(err, 'Durum güncellenemedi.');
      return { ok: false, message: msg };
    } finally {
      setUpdatingStatus(false);
    }
  }, [threadId]);

  useEffect(() => {
    if (!threadId) return undefined;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetchFeedbackThreadMessages(getRef.current, threadId, {
          limit: MESSAGE_PAGE_SIZE,
          offset: 0,
        });
        const payload = res?.data ?? res;
        const items = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.messages)
            ? payload.messages
            : Array.isArray(payload)
              ? payload
              : [];
        const mapped = items.map(mapMessage).filter(Boolean);
        if (!mapped.length) return;

        setDetail((prev) => {
          if (!prev.thread) return prev;
          const nextMessages = mergeMessages(prev.messages, mapped);
          if (nextMessages.length === prev.messages.length) {
            const prevLast = prev.messages[prev.messages.length - 1]?.id;
            const nextLast = nextMessages[nextMessages.length - 1]?.id;
            if (prevLast === nextLast) {
              return prev;
            }
          }
          const lastMessage = nextMessages[nextMessages.length - 1] ?? null;
          return {
            thread: {
              ...prev.thread,
              updatedAt: payload?.thread?.updated_at ?? lastMessage?.createdAt ?? prev.thread.updatedAt,
            },
            messages: nextMessages,
            total: payload?.total ?? prev.total ?? nextMessages.length,
          };
        });
      } catch {
        // swallow polling errors
      }
    };

    const interval = setInterval(poll, POLL_INTERVAL);
    poll();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [threadId]);

  return {
    detail,
    loading,
    error,
    sending,
    load,
    refresh: load,
    sendMessage,
    loadMore,
    loadingMore,
    hasMore: (detail?.messages?.length ?? 0) < (detail?.total ?? 0),
    updatingStatus,
    changeStatus,
  };
}
